import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Progress,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  MegaphoneIcon,
  PlusIcon,
  SearchIcon,
  SmartphoneIcon,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { CampaignSheet } from '@/components/campaigns/CampaignSheet'
import { useAuth } from '@/hooks/use-auth'
import { useCampaigns } from '@/hooks/use-campaigns'
import { useChannels } from '@/hooks/use-channels'
import { CAMPAIGN_STATUS_TONE } from '@/lib/status-tones'
import type { Campaign, CampaignStatus } from '@/types/campaigns'

export const Route = createFileRoute('/_app/campanhas/')({
  component: CampanhasPage,
})

const STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Rascunho',
  SCHEDULED: 'Agendada',
  RUNNING: 'Enviando',
  PAUSED: 'Pausada',
  COMPLETED: 'Concluída',
  CANCELLED: 'Cancelada',
  FAILED: 'Falhou',
}

const STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'Todas', value: 'all' },
  { label: 'Rascunho', value: 'DRAFT' },
  { label: 'Enviando', value: 'RUNNING' },
  { label: 'Pausada', value: 'PAUSED' },
  { label: 'Concluída', value: 'COMPLETED' },
  { label: 'Cancelada', value: 'CANCELLED' },
]

const ALL = '__all__'

function CampanhasPage() {
  const { currentOrgId } = useAuth()
  const navigate = useNavigate()
  const { data: channels = [] } = useChannels(currentOrgId)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [channelId, setChannelId] = useState<string>(ALL)
  const [status, setStatus] = useState('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)

  // Debounce da busca.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  // Qualquer mudança de filtro volta à primeira página.
  useEffect(() => {
    setPage(1)
  }, [channelId, status, search, from, to])

  const filters = {
    page,
    ...(channelId !== ALL ? { channelId } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(search ? { search } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }
  const { data: response, isLoading } = useCampaigns(currentOrgId, filters)
  const campaigns = response?.data ?? []

  const total = response?.total ?? response?.meta?.total ?? 0
  const limit = response?.limit ?? response?.meta?.limit ?? 20
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const channelName = useMemo(
    () => new Map(channels.map((c) => [c.id, c.name])),
    [channels]
  )
  const hasFilters =
    channelId !== ALL || status !== 'all' || !!search || !!from || !!to

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={MegaphoneIcon}
        title="Campanhas"
        description="Disparos em massa por canal com templates ou texto livre"
        actions={
          <>
            <Button onClick={() => setSheetOpen(true)}>
              <PlusIcon className="h-4 w-4" />
              Nova campanha
            </Button>
          </>
        }
      />

      <main className="min-h-0 flex-1 space-y-6 overflow-auto p-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-56 flex-1">
              <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar campanha..."
                className="pl-9"
              />
            </div>
            <SegmentedControl
              label="Status"
              options={STATUS_FILTERS}
              value={status}
              onChange={setStatus}
            />
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Canal</Label>
              <Select value={channelId} onValueChange={setChannelId}>
                <SelectTrigger className="h-9 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os canais</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">De</Label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="h-9 w-40"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Até</Label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="h-9 w-40"
              />
            </div>
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setChannelId(ALL)
                  setStatus('all')
                  setSearchInput('')
                  setSearch('')
                  setFrom('')
                  setTo('')
                }}
              >
                Limpar
              </Button>
            ) : null}
          </div>
        </div>

        <CampaignSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          orgId={currentOrgId}
          onCreated={(campaignId) =>
            void navigate({
              to: '/campanhas/$campaignId',
              params: { campaignId },
            })
          }
        />

        {isLoading ? (
          <LoadingState />
        ) : campaigns.length === 0 ? (
          <EmptyState
            icon={MegaphoneIcon}
            title={hasFilters ? 'Nada encontrado' : 'Nenhuma campanha ainda'}
            description={
              hasFilters
                ? 'Nenhuma campanha para os filtros selecionados.'
                : 'Crie a primeira para começar.'
            }
          />
        ) : (
          <>
            <div className="grid gap-3">
              {campaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  channelName={channelName.get(campaign.channelId)}
                  onOpen={() =>
                    void navigate({
                      to: '/campanhas/$campaignId',
                      params: { campaignId: campaign.id },
                    })
                  }
                />
              ))}
            </div>

            {totalPages > 1 ? (
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground text-sm">
                  {(page - 1) * limit + 1}-{Math.min(page * limit, total)} de{' '}
                  {total}
                </p>
                <Pagination className="mx-0 w-auto">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        aria-disabled={page <= 1}
                        className={
                          page <= 1 ? 'pointer-events-none opacity-50' : ''
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          setPage((p) => Math.max(1, p - 1))
                        }}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalPages })
                      .map((_, i) => i + 1)
                      .filter(
                        (n) =>
                          n === 1 || n === totalPages || Math.abs(n - page) <= 1
                      )
                      .map((n) => (
                        <PaginationItem key={n}>
                          <PaginationLink
                            isActive={n === page}
                            onClick={(e) => {
                              e.preventDefault()
                              setPage(n)
                            }}
                          >
                            {n}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                    <PaginationItem>
                      <PaginationNext
                        aria-disabled={page >= totalPages}
                        className={
                          page >= totalPages
                            ? 'pointer-events-none opacity-50'
                            : ''
                        }
                        onClick={(e) => {
                          e.preventDefault()
                          setPage((p) => Math.min(totalPages, p + 1))
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <span>
      <span className="text-foreground font-medium">{value}</span>{' '}
      <span className="text-muted-foreground">{label}</span>
    </span>
  )
}

function CampaignRow({
  campaign,
  channelName,
  onOpen,
}: {
  campaign: Campaign
  channelName?: string
  onOpen: () => void
}) {
  const done = campaign.sent + campaign.failed + campaign.skipped
  const progress =
    campaign.totalRecipients > 0
      ? Math.round((done / campaign.totalRecipients) * 100)
      : 0
  return (
    <Card
      className="hover:bg-muted/30 cursor-pointer transition-colors"
      onClick={onOpen}
    >
      <CardContent className="space-y-3 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{campaign.name}</span>
              <Badge variant="outline" className="text-2xs">
                {campaign.contentMode === 'TEMPLATE'
                  ? 'Template'
                  : 'Texto livre'}
              </Badge>
            </div>
            <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
              <span className="flex items-center gap-1">
                <SmartphoneIcon className="size-3" /> {channelName ?? 'Canal'}
              </span>
              <span>·</span>
              <span>
                Criada em{' '}
                {new Date(campaign.createdAt).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>
          <StatusBadge tone={CAMPAIGN_STATUS_TONE[campaign.status]}>
            {STATUS_LABELS[campaign.status]}
          </StatusBadge>
        </div>

        {campaign.totalRecipients > 0 ? (
          <div className="space-y-1">
            <div className="text-muted-foreground flex items-center justify-between text-xs">
              <span>Progresso</span>
              <span>
                {done}/{campaign.totalRecipients} ({progress}%)
              </span>
            </div>
            <Progress value={progress} />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <Stat label="Destinatários" value={campaign.totalRecipients} />
          <Stat label="Enviadas" value={campaign.sent} />
          <Stat label="Entregues" value={campaign.delivered} />
          <Stat label="Falhas" value={campaign.failed} />
          <Stat label="Puladas" value={campaign.skipped} />
        </div>
      </CardContent>
    </Card>
  )
}
